/* eslint-disable react/button-has-type */
import { type ButtonHTMLAttributes, type JSX } from 'react';
import { getButtonAriaProps } from '@common/components/controls/ariaHelpers/buttonAriaHelpers';
import { type RequiredTestId } from '@common/components/controls/TestId';
import Icon, { type IconComponentType } from '@common/components/Icon';
import IconSpinner from '@common/components/svgs/common/Spinner';
import IconStatusCheck from '@common/components/svgs/status/Check';
import { type IconColor } from '@common/types/colors';
import classnames from '@common/utils/ui/classnames';
import createRipple from '@common/utils/ui/ripple';
import '@common/styles/components/button.pcss';

export type ButtonLoadingState = undefined | 'loading' | 'finished';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  mode?: 'secondary' | 'dangerous' | 'text' | 'text-link' | 'text-dangerous' | 'hidden' | 'link';
  loadingState?: ButtonLoadingState;
  loadingStateContent?: JSX.Element | string;
  finishedStateContent?: JSX.Element | string;
  isFullWidth?: boolean;
  isTextCentered?: boolean;
  leadingIconComponent?: IconComponentType;
  leadingIconSize?: 16 | 24;
  forbidden?: boolean;
  disableAutoCapture?: boolean;
}

function Button({
  mode,
  children,
  disabled,
  loadingState,
  loadingStateContent,
  finishedStateContent,
  type = 'submit',
  isFullWidth = false,
  isTextCentered = false,
  leadingIconComponent,
  leadingIconSize,
  forbidden,
  onClick,
  disableAutoCapture,
  'data-testid': testId,
  ...rest
}: RequiredTestId<ButtonProps>) {
  let content = children;
  if (loadingState) {
    let iconColor: IconColor = 'action';
    if (mode === 'dangerous' || mode === 'text-dangerous') {
      iconColor = 'error';
    } else if (mode === 'secondary') {
      iconColor = 'regular';
    }
    if (loadingState === 'finished') {
      content = (
        <>
          <Icon component={IconStatusCheck} color={iconColor} size={24} />
          {finishedStateContent ?? children}
        </>
      );
    }
    if (loadingState === 'loading') {
      content = (
        <>
          <Icon component={IconSpinner} color={iconColor} size={16} />
          {loadingStateContent ?? children}
        </>
      );
    }
  }
  let buttonContent = content;
  if (leadingIconComponent) {
    buttonContent = (
      <>
        <Icon
          component={leadingIconComponent}
          size={leadingIconSize}
        />
        {content}
      </>
    );
  }

  const ariaProps = getButtonAriaProps({
    disabled,
    isLoading: loadingState === 'loading',
    forbidden,
  });

  return (
    <button
      {...rest}
      {...ariaProps}
      className={classnames({
        button: true,
        [`button--type-${mode || 'default'}`]: true, // NOSONAR:S6606 - Empty string mode should use default
        'button--state-loading': !!loadingState,
        'button--full-width': isFullWidth,
        'button--text-centered': isTextCentered,
        'button--forbidden': !disabled && forbidden,
        'ph-no-capture': disableAutoCapture,
      })}
      type={type}
      disabled={disabled || forbidden || !!loadingState}
      onClick={(event) => {
        createRipple(event);
        event.stopPropagation();

        if (typeof onClick === 'function') {
          onClick(event);
        }
      }}
      data-testid={testId}
    >
      {buttonContent}
    </button>
  );
}

export default Button;
